import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Upload, Trash2, Eye, Loader2, Sparkles, FileBarChart, ExternalLink, ShieldCheck, Download, Share2, UserCog, MessageCircle, CheckCircle2, Pill, ReceiptText } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import HealthAnalysisView from "@/components/HealthAnalysisView";
import HealthInsightsPanel from "@/components/health/HealthInsightsPanel";
import UploadRecordDialog from "@/components/health/UploadRecordDialog";
import PatientSummaryDialog from "@/components/health/PatientSummaryDialog";
import { healthApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import { useConsentStore } from "@/store/consentStore";

const DISPLAY_CATEGORIES = [
  { id: "doctor_consultation", name: "Consultations", icon: "👨‍⚕️", color: "bg-emerald-50" },
  { id: "lab_report", name: "Lab Reports", icon: "🧪", color: "bg-sky-50" },
  { id: "imaging", name: "Imaging/Scans", icon: "🔬", color: "bg-pink-50" },
  { id: "other", name: "Other", icon: "📄", color: "bg-slate-50" },
];
const DOC_ICON = { doctor_consultation: "👨‍⚕️", prescription: "👨‍⚕️", consultation: "👨‍⚕️", consultation_notes: "👨‍⚕️", lab_report: "🧪", discharge_summary: "🏥", imaging: "🔬", imaging_report: "🔬", other: "📄" };
const docIcon = (t) => DOC_ICON[t] || "📄";

const DATE_CHIPS = [
  { id: "all", label: "🗓️ All Time" },
  { id: "thisMonth", label: "📅 This Month" },
  { id: "last3", label: "⏱️ Last 3 Months" },
  { id: "thisYear", label: "📆 This Year" },
  { id: "older", label: "🗄️ Older" },
];

function buildRecordText(r) {
  const a = r.aiAnalysis || {};
  let t = `📋 ${r.documentName}\n`;
  if (r.uploadDate) t += `📅 ${new Date(r.uploadDate).toLocaleDateString()}\n`;
  if (a.summary || r.summary) t += `\n📝 Summary\n${a.summary || r.summary}\n`;
  if (a.keyFindings?.length) t += `\n🔍 Key findings\n${a.keyFindings.map((f) => `• ${f}`).join("\n")}\n`;
  if (a.flaggedValues?.length) t += `\n⚠️ Flagged values\n${a.flaggedValues.map((v) => `• ${v.name}: ${v.value} (${v.status})`).join("\n")}\n`;
  if (a.recommendations?.length) t += `\n💡 Recommendations\n${a.recommendations.map((x) => `• ${x}`).join("\n")}\n`;
  return t + `\n— Trata Health Records`;
}

function printRecordPdf(r) {
  const a = r.aiAnalysis || {};
  const sec = (title, body) => (body ? `<div class="s"><h3>${title}</h3>${body}</div>` : "");
  const ul = (arr) => (arr?.length ? `<ul>${arr.map((x) => `<li>${typeof x === "string" ? x : x.step || x.name || JSON.stringify(x)}</li>`).join("")}</ul>` : "");
  const flagged = a.flaggedValues?.length ? `<table><tr><th>Test</th><th>Value</th><th>Status</th></tr>${a.flaggedValues.map((v) => `<tr><td>${v.name || v.testName || ""}</td><td>${v.value || ""}</td><td>${(v.status || "").toUpperCase()}</td></tr>`).join("")}</table>` : "";
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${r.documentName}</title>
  <style>body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;color:#1f2937;padding:28px;max-width:720px;margin:auto}
  .h{background:linear-gradient(135deg,#2563eb,#1e40af);color:#fff;padding:22px;border-radius:14px}
  .s{border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin-top:14px}h3{margin:0 0 8px}
  table{width:100%;border-collapse:collapse;font-size:13px}th,td{text-align:left;padding:7px;border-bottom:1px solid #eee}
  ul{padding-left:20px;margin:0}li{margin-bottom:6px;line-height:1.5}.f{text-align:center;color:#9ca3af;font-size:11px;margin-top:24px}</style></head><body>
  <div class="h"><h1 style="margin:0;font-size:22px">${r.documentName || "Medical Record"}</h1>
  <p style="margin:6px 0 0;opacity:.9;font-size:13px">${r.patientName || "You"}${r.uploadDate ? " · " + new Date(r.uploadDate).toLocaleDateString() : ""}</p></div>
  ${sec("Summary", (a.summary || r.summary) ? `<p>${a.summary || r.summary}</p>` : "")}
  ${sec("Diagnosis", ul(a.diagnosis))}
  ${sec("Flagged Values", flagged)}
  ${sec("Key Findings", ul(a.keyFindings))}
  ${sec("Medications", a.medications?.length ? `<ul>${a.medications.map((m) => `<li><b>${m.name}</b> ${[m.dosage, m.frequency, m.duration].filter(Boolean).join(" · ")}</li>`).join("")}</ul>` : "")}
  ${sec("Recommendations", ul(a.recommendations))}
  <div class="f">Generated by Trata Health · For informational purposes only · Consult your doctor</div>
  <script>window.onload=function(){window.print();}</script></body></html>`;
  const w = window.open("", "_blank");
  if (!w) { toast.error("Allow pop-ups to download the PDF"); return; }
  w.document.write(html);
  w.document.close();
}

export default function HealthRecords() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const openFeatureGate = useConsentStore((s) => s.openFeatureGate);

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [patientFilters, setPatientFilters] = useState([]);
  const [groupedRecords, setGroupedRecords] = useState({});
  const [medConditions, setMedConditions] = useState(null);

  const [mainTab, setMainTab] = useState("summary");
  const [viewMode, setViewMode] = useState("date");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [selectedDateFilter, setSelectedDateFilter] = useState("all");
  const [selectedPatient, setSelectedPatient] = useState("all");

  const [detailRecord, setDetailRecord] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [showPatientSummary, setShowPatientSummary] = useState(false);
  const [patientSummary, setPatientSummary] = useState(null);
  const [patientSummaryLoading, setPatientSummaryLoading] = useState(false);
  const [patientSummaryName, setPatientSummaryName] = useState("");

  const [reassignRecord, setReassignRecord] = useState(null);
  const [reassignLoading, setReassignLoading] = useState(false);

  const loadMedConditions = async () => {
    if (!user?._id) return;
    try {
      const res = await healthApi.medicationsConditions(user._id);
      setMedConditions(res.data?.summary || null);
    } catch (e) { setMedConditions(null); }
  };

  const loadRecords = async () => {
    if (!user?._id) return;
    try {
      const res = await healthApi.records(user._id);
      setRecords(res.data?.records || []);
      setPatientFilters(res.data?.patientFilters || []);
      setGroupedRecords(res.data?.groupedRecords || {});
    } catch (e) {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
    loadMedConditions();
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh while any record is still analyzing
  useEffect(() => {
    if (!records.some((r) => r.status === "processing")) return;
    const t = setTimeout(() => { loadRecords(); }, 5000);
    return () => clearTimeout(t);
  }, [records]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadClick = async () => {
    const granted = await openFeatureGate();
    if (!granted) { toast.info("Consent is required to analyze your health records."); return; }
    setShowUpload(true);
  };

  const getFilteredRecords = () => {
    let result = records;
    if (selectedPatient !== "all") {
      const p = patientFilters.find((x) => x.id === selectedPatient);
      if (p) {
        if (p.type === "self") result = records.filter((r) => r.patientType === "self" || (r.patientName && p.name && r.patientName.toLowerCase() === p.name.toLowerCase()));
        else if (p.type === "dependent") result = records.filter((r) => r.dependentId === p.id || (r.patientName && p.name && r.patientName.toLowerCase() === p.name.toLowerCase()));
        else if (p.type === "unidentified") result = records.filter((r) => !r.patientName || r.patientName === "Unidentified");
      }
    }
    if (viewMode === "type" && selectedFilter !== "all") {
      result = result.filter((r) => {
        const dt = r.documentType;
        if (selectedFilter === "doctor_consultation") return ["doctor_consultation", "prescription", "consultation", "consultation_notes"].includes(dt);
        if (selectedFilter === "consult_only") return ["doctor_consultation", "consultation", "consultation_notes"].includes(dt);
        if (selectedFilter === "labs_combined") return ["lab_report", "imaging", "imaging_report"].includes(dt);
        if (selectedFilter === "imaging") return ["imaging", "imaging_report"].includes(dt);
        return dt === selectedFilter;
      });
    }
    if (viewMode === "date" && selectedDateFilter !== "all") {
      const now = new Date();
      result = result.filter((r) => {
        const d = new Date(r.uploadDate);
        if (isNaN(d.getTime())) return false;
        if (selectedDateFilter === "thisMonth") return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        if (selectedDateFilter === "last3") return now.getTime() - d.getTime() <= 90 * 864e5;
        if (selectedDateFilter === "thisYear") return d.getFullYear() === now.getFullYear();
        if (selectedDateFilter === "older") return d.getFullYear() < now.getFullYear();
        return true;
      });
    }
    return result;
  };

  const typeCount = (id) => {
    if (id === "doctor_consultation") return ["doctor_consultation", "prescription", "consultation", "consultation_notes"].reduce((s, k) => s + (groupedRecords[k]?.length || 0), 0);
    if (id === "imaging") return (groupedRecords.imaging?.length || 0) + (groupedRecords.imaging_report?.length || 0);
    return groupedRecords[id]?.length || 0;
  };

  const openSummary = async () => {
    if (records.length === 0) { toast.info("Upload a report first to see your overall summary."); return; }
    setShowSummary(true);
    setSummaryLoading(true);
    try {
      const res = await healthApi.overallSummary(user._id);
      setSummary(res.data?.summary || null);
    } catch (e) { setSummary(null); toast.error("Could not load summary"); } finally { setSummaryLoading(false); }
  };

  const openPatientSummary = async (member) => {
    setPatientSummaryName(member.name);
    setShowPatientSummary(true);
    setPatientSummaryLoading(true);
    setPatientSummary(null);
    try {
      const res = await healthApi.patientSummary(user._id, member.id === "self" ? "self" : member.id);
      setPatientSummary(res.data?.summary || null);
    } catch (e) { toast.error("Could not load summary"); } finally { setPatientSummaryLoading(false); }
  };

  const view = async (r) => {
    try {
      const res = await healthApi.file(r._id, user._id);
      if (res.data?.signed_url) window.open(res.data.signed_url, "_blank");
      else if (res.data?.file_data) {
        const src = `data:${res.data.file_type};base64,${res.data.file_data}`;
        const w = window.open();
        if (w) w.document.write(`<iframe src="${src}" style="border:0;width:100%;height:100%"></iframe>`);
      } else toast.info("No file stored for this record.");
    } catch (e) { toast.error("Could not open file"); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.documentName}"?`)) return;
    try {
      await healthApi.deleteRecord(r._id, user._id);
      setRecords((prev) => prev.filter((x) => x._id !== r._id));
      setDetailRecord(null);
      toast.success("Record deleted");
    } catch (e) { toast.error("Could not delete record"); }
  };

  const shareRecord = async (r) => {
    const text = buildRecordText(r);
    try {
      if (navigator.share) await navigator.share({ title: r.documentName, text });
      else { await navigator.clipboard.writeText(text); toast.success("Summary copied to clipboard"); }
    } catch (e) { /* user cancelled */ }
  };

  const discussWithDr = (r) => {
    const a = r.aiAnalysis || {};
    const title = a.documentTypeName || r.documentName || "my medical report";
    const lines = [`Report: ${title}`];
    if (Array.isArray(a.diagnosis) && a.diagnosis.length) lines.push(`Diagnosis: ${a.diagnosis.join("; ")}`);
    if (a.detailedExplanation) lines.push(`Explanation: ${a.detailedExplanation}`);
    else if (a.summary) lines.push(`Summary: ${a.summary}`);
    if (Array.isArray(a.keyFindings) && a.keyFindings.length) lines.push(`Key findings: ${a.keyFindings.join("; ")}`);
    if (Array.isArray(a.flaggedValues) && a.flaggedValues.length) lines.push(`Flagged values: ${a.flaggedValues.slice(0, 12).map((v) => `${v.name || v.testName || "Value"}: ${v.value || ""} (${v.status || ""})`).join("; ")}`);
    if (Array.isArray(a.recommendations) && a.recommendations.length) lines.push(`Recommendations: ${a.recommendations.join("; ")}`);
    localStorage.setItem("ds_report_context", lines.join("\n").slice(0, 3500));
    localStorage.setItem("ds_report_title", title);
    setDetailRecord(null);
    navigate("/assistant?discussReport=1");
  };

  const doReassign = async (member) => {
    if (!reassignRecord) return;
    setReassignLoading(true);
    try {
      await healthApi.updateRecordPatient(reassignRecord._id, user._id, {
        patientName: member.name,
        patientType: member.type === "self" ? "self" : "dependent",
        dependentId: member.type === "dependent" ? member.id : null,
        relation: member.relation || (member.type === "self" ? "self" : null),
      });
      toast.success(`Assigned to ${member.name}`);
      setReassignRecord(null);
      loadRecords();
    } catch (e) { toast.error("Could not reassign record"); } finally { setReassignLoading(false); }
  };

  const filtered = getFilteredRecords();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold">Health Records</h1>
          <p className="text-muted-foreground mt-1">AI-powered insights from your medical documents.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-2xl" onClick={openSummary} disabled={records.length === 0} data-testid="overall-summary-button">
            <FileBarChart className="h-4 w-4" /> Summary
          </Button>
          <Button className="rounded-2xl" onClick={handleUploadClick} data-testid="upload-record-button">
            <Upload className="h-4 w-4" /> Upload
          </Button>
        </div>
      </div>

      {/* Clickable stat boxes — tap to open that record type (moved above Prescriptions) */}
      {records.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stat-tiles-row">
          {[
            { label: "All Records", value: records.length, filter: "all", testid: "stat-tile-all", accent: "text-indigo-600" },
            { label: "Consultations", value: ["doctor_consultation", "consultation", "consultation_notes"].reduce((s, k) => s + (groupedRecords[k]?.length || 0), 0), filter: "consult_only", testid: "stat-tile-consult_only", accent: "text-blue-600" },
            { label: "Lab Reports", value: (groupedRecords.lab_report?.length || 0) + (groupedRecords.imaging?.length || 0) + (groupedRecords.imaging_report?.length || 0), filter: "labs_combined", testid: "stat-tile-labs_combined", accent: "text-emerald-600" },
            { label: "Prescriptions", value: groupedRecords.prescription?.length || 0, filter: "prescription", testid: "stat-tile-prescription", accent: "text-amber-600" },
          ].map((tile) => (
            <Card
              key={tile.filter}
              onClick={() => { setMainTab("records"); setViewMode("type"); setSelectedFilter(tile.filter); }}
              data-testid={tile.testid}
              className="rounded-2xl p-4 text-center cursor-pointer hover:bg-accent transition-colors"
            >
              <p className={`text-2xl font-bold ${tile.accent}`}>{tile.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{tile.label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* Prescriptions & Pharmacy quick access (moved from For You) */}
      <Card
        onClick={() => navigate("/medications?tab=prescriptions")}
        data-testid="prescriptions-entry-card"
        className="rounded-3xl p-4 flex items-center gap-3 cursor-pointer hover:bg-accent transition-colors"
      >
        <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
          <Pill className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium">Prescriptions & Pharmacy</p>
          <p className="text-sm text-muted-foreground">Reorder medicines & view receipts</p>
        </div>
        <ReceiptText className="h-5 w-5 text-muted-foreground shrink-0" />
      </Card>

      {/* ABHA Health ID (ABDM) */}
      <Card
        onClick={() => navigate("/abha")}
        data-testid="abha-entry-card"
        className="rounded-3xl p-4 flex items-center gap-3 cursor-pointer hover:bg-accent transition-colors"
      >
        <div className="h-11 w-11 rounded-2xl bg-emerald-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium">ABHA Health ID</p>
          <p className="text-sm text-muted-foreground">Create or link your Ayushman Bharat health account</p>
        </div>
        <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0" />
      </Card>

      {/* Share records with ABHA (ABDM M2 / HIP) */}
      <Card
        onClick={() => navigate("/abha/share")}
        data-testid="share-abha-entry-card"
        className="rounded-3xl p-4 flex items-center gap-3 cursor-pointer hover:bg-accent transition-colors"
      >
        <div className="h-11 w-11 rounded-2xl bg-sky-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-sky-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium">Share Records with ABHA</p>
          <p className="text-sm text-muted-foreground">Link consultations, lab reports & prescriptions to your health account</p>
        </div>
        <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0" />
      </Card>

      {/* Fetch records from other ABDM providers (ABDM M3 / HIU) */}
      <Card
        onClick={() => navigate("/abha/fetch")}
        data-testid="fetch-abha-entry-card"
        className="rounded-3xl p-4 flex items-center gap-3 cursor-pointer hover:bg-accent transition-colors"
      >
        <div className="h-11 w-11 rounded-2xl bg-indigo-100 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-indigo-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium">Fetch Records via ABHA</p>
          <p className="text-sm text-muted-foreground">Pull your records from other ABDM hospitals & labs with consent</p>
        </div>
        <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0" />
      </Card>

      <Tabs value={mainTab} onValueChange={setMainTab}>
        <TabsList className="rounded-2xl">
          <TabsTrigger value="summary" className="rounded-xl" data-testid="tab-summary"><Sparkles className="h-4 w-4 mr-1.5" /> AI Summary</TabsTrigger>
          <TabsTrigger value="records" className="rounded-xl" data-testid="tab-records"><FileText className="h-4 w-4 mr-1.5" /> Records ({records.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 rounded-3xl" />)}</div>
      ) : mainTab === "summary" ? (
        <HealthInsightsPanel records={records} medConditions={medConditions} onUpload={handleUploadClick} patientFilters={patientFilters} onOpenPatientSummary={openPatientSummary} />
      ) : (
        <div className="space-y-4">
          <button type="button" onClick={handleUploadClick} className="w-full flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-4 text-left hover:bg-emerald-100 transition-colors" data-testid="upload-card">
            <div className="h-11 w-11 rounded-2xl bg-emerald-100 flex items-center justify-center"><Upload className="h-5 w-5 text-emerald-600" /></div>
            <div className="flex-1"><p className="font-semibold text-emerald-900">Upload Report / Prescription</p><p className="text-xs text-emerald-700">AI reads, summarizes & stores securely</p></div>
          </button>

          {patientFilters.length > 2 && (
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="patient-filter-row">
              {patientFilters.map((p) => (
                <button key={p.id} type="button" onClick={() => setSelectedPatient(p.id)} className={`shrink-0 text-sm px-3 py-1.5 rounded-full border capitalize ${selectedPatient === p.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background"}`} data-testid={`patient-filter-${p.id}`}>
                  {p.type === "self" ? "👤" : p.type === "dependent" ? "👥" : p.type === "unidentified" ? "❓" : "👨‍👩‍👧"} {p.name} ({p.count})
                </button>
              ))}
            </div>
          )}

          {records.length > 0 && (
            <>
              <div className="inline-flex rounded-2xl bg-muted p-1">
                {["date", "type"].map((m) => (
                  <button key={m} type="button" onClick={() => setViewMode(m)} className={`px-4 py-1.5 rounded-xl text-sm font-medium ${viewMode === m ? "bg-background shadow-sm text-foreground" : "text-muted-foreground"}`} data-testid={`view-mode-${m}`}>
                    {m === "date" ? "By Date" : "By Type"}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="filter-chip-row">
                {viewMode === "date"
                  ? DATE_CHIPS.map((c) => (
                      <button key={c.id} type="button" onClick={() => setSelectedDateFilter(c.id)} className={`shrink-0 text-sm px-3 py-1.5 rounded-full border ${selectedDateFilter === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} data-testid={`date-chip-${c.id}`}>{c.label}</button>
                    ))
                  : (
                    <>
                      <button type="button" onClick={() => setSelectedFilter("all")} className={`shrink-0 text-sm px-3 py-1.5 rounded-full border ${selectedFilter === "all" ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} data-testid="type-chip-all">All ({records.length})</button>
                      {DISPLAY_CATEGORIES.map((c) => (
                        <button key={c.id} type="button" onClick={() => setSelectedFilter(c.id)} className={`shrink-0 text-sm px-3 py-1.5 rounded-full border ${selectedFilter === c.id ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} data-testid={`type-chip-${c.id}`}>{c.icon} {c.name} ({typeCount(c.id)})</button>
                      ))}
                    </>
                  )}
              </div>
            </>
          )}

          {filtered.length === 0 ? (
            <Card className="rounded-3xl p-10 text-center border-dashed" data-testid="records-empty">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
              <p className="mt-4 font-medium">No records yet</p>
              <p className="text-sm text-muted-foreground mt-1">Upload a lab report, prescription or scan to get started.</p>
              <Button className="mt-5 rounded-2xl" onClick={handleUploadClick} data-testid="empty-upload-button"><Upload className="h-4 w-4" /> Upload your first report</Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {filtered.map((r) => {
                const hasAnalysis = r.status !== "processing" && ((r.aiAnalysis && Object.keys(r.aiAnalysis).length > 0) || r.summary);
                return (
                  <Card key={r._id} className="rounded-3xl p-4" data-testid={`record-${r._id}`}>
                    <div className="flex items-center gap-3">
                      <button type="button" disabled={!hasAnalysis} onClick={() => hasAnalysis && setDetailRecord(r)} className="flex items-center gap-3 min-w-0 flex-1 text-left disabled:cursor-default" data-testid={`open-record-${r._id}`}>
                        <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center shrink-0 text-lg">{docIcon(r.documentType)}</div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{r.documentName}</p>
                          <p className="text-sm text-muted-foreground truncate">{r.patientName || "You"}{r.uploadDate ? " • " + new Date(r.uploadDate).toLocaleDateString() : ""}</p>
                        </div>
                      </button>
                      <button type="button" onClick={() => setReassignRecord(r)} className="shrink-0 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground flex items-center gap-1 hover:bg-accent" data-testid={`reassign-${r._id}`} title="Assign patient">
                        <UserCog className="h-3.5 w-3.5" /> {r.patientName ? "Edit" : "Assign"}
                      </button>
                      {r.status === "processing" ? (
                        <span className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Analyzing</span>
                      ) : (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => view(r)} title="View original file" data-testid={`view-${r._id}`}><Eye className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" className="rounded-full text-destructive" onClick={() => remove(r)} data-testid={`delete-${r._id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </div>
                    {r.summary && (
                      <button type="button" onClick={() => hasAnalysis && setDetailRecord(r)} className="mt-3 w-full flex items-start gap-2 text-sm text-muted-foreground bg-muted/50 rounded-2xl p-3 text-left hover:bg-muted transition-colors" data-testid={`summary-${r._id}`}>
                        <Sparkles className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <span className="flex-1 line-clamp-3">{r.summary}</span>
                      </button>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Upload */}
      <UploadRecordDialog open={showUpload} onOpenChange={(o) => { setShowUpload(o); if (!o) { loadRecords(); loadMedConditions(); } }} userId={user?._id} patientFilters={patientFilters} onUploaded={() => { loadRecords(); loadMedConditions(); }} />

      {/* Patient AI summary */}
      <PatientSummaryDialog open={showPatientSummary} onOpenChange={setShowPatientSummary} loading={patientSummaryLoading} summary={patientSummary} patientName={patientSummaryName} />

      {/* Reassign */}
      <Dialog open={!!reassignRecord} onOpenChange={(o) => !o && setReassignRecord(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="reassign-dialog">
          <DialogHeader><DialogTitle className="text-left">Assign to patient</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {patientFilters.filter((p) => p.type === "self" || p.type === "dependent").map((m) => (
              <button key={m.id} type="button" disabled={reassignLoading} onClick={() => doReassign(m)} className="w-full flex items-center gap-3 rounded-2xl border border-border p-3 text-left hover:bg-muted disabled:opacity-50" data-testid={`reassign-to-${m.id}`}>
                <span className="text-xl">{m.type === "self" ? "👤" : "👥"}</span>
                <span className="font-medium flex-1 capitalize">{m.name}{m.relation ? ` · ${m.relation}` : ""}</span>
                {reassignRecord?.dependentId === m.id && <CheckCircle2 className="h-4 w-4 text-primary" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Record detail */}
      <Dialog open={!!detailRecord} onOpenChange={(o) => !o && setDetailRecord(null)}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto rounded-3xl" data-testid="record-detail-dialog">
          <DialogHeader>
            <DialogTitle className="pr-6 text-left">{detailRecord?.documentName}</DialogTitle>
            <DialogDescription className="text-left">{detailRecord?.patientName || "You"}{detailRecord?.uploadDate ? " • " + new Date(detailRecord.uploadDate).toLocaleDateString() : ""}</DialogDescription>
          </DialogHeader>
          {detailRecord && (
            <>
              <HealthAnalysisView ai={detailRecord.aiAnalysis} summaryFallback={detailRecord.summary} />
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="rounded-2xl" onClick={() => view(detailRecord)} data-testid="detail-view-original"><ExternalLink className="h-4 w-4" /> Original</Button>
                <Button variant="outline" className="rounded-2xl" onClick={() => printRecordPdf(detailRecord)} data-testid="detail-download-pdf"><Download className="h-4 w-4" /> Download PDF</Button>
                <Button variant="outline" className="rounded-2xl" onClick={() => shareRecord(detailRecord)} data-testid="detail-share"><Share2 className="h-4 w-4" /> Share</Button>
                <Button className="rounded-2xl" onClick={() => discussWithDr(detailRecord)} data-testid="detail-discuss"><MessageCircle className="h-4 w-4" /> Ask Dr Sanjeevani</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Overall summary */}
      <Dialog open={showSummary} onOpenChange={setShowSummary}>
        <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto rounded-3xl" data-testid="summary-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-left"><FileBarChart className="h-5 w-5 text-primary" /> Your Health Summary</DialogTitle>
            <DialogDescription className="text-left">AI-aggregated overview across all your reports.</DialogDescription>
          </DialogHeader>
          {summaryLoading ? (
            <div className="py-10 flex flex-col items-center gap-3"><Loader2 className="h-6 w-6 animate-spin text-primary" /><p className="text-sm text-muted-foreground">Generating your summary…</p></div>
          ) : !summary || Object.keys(summary.problemGroups || {}).length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No analyzed reports yet.</p>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{summary.totalRecords} report{summary.totalRecords === 1 ? "" : "s"} analyzed.</p>
              {Object.entries(summary.problemGroups).map(([key, g]) => (
                <div key={key} className="rounded-2xl border border-border p-4" data-testid={`summary-group-${key}`}>
                  <p className="font-semibold flex items-center gap-2"><span>{g.icon}</span> {g.name} <span className="text-xs text-muted-foreground">({g.records?.length || 0})</span></p>
                  {g.keyFindings?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Key findings</p>
                      {g.keyFindings.slice(0, 5).map((f, i) => (<div key={i} className="flex items-start text-sm text-slate-700"><span className="text-sky-500 mr-2">•</span><span className="flex-1">{f}</span></div>))}
                    </div>
                  )}
                  {g.flaggedValues?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {g.flaggedValues.slice(0, 8).map((v, i) => (<span key={i} className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-100">{v.name || v.testName}: {v.value}</span>))}
                    </div>
                  )}
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5" /> This summary is informational and not a substitute for professional medical advice.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
